'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import { Slot } from '@radix-ui/react-slot';
import { VariantProps, cva } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';

import { useChromeMobile } from '@hooks/use-mobile';
import { cn } from '@utils/cn';
import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_INSET,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_OPEN_COOKIE,
  SIDEBAR_WIDTH_COOKIE,
  clampSidebarWidth,
  resolveSidebarDrag,
  sidebarWidthForKey,
  writeSidebarCookie
} from '@components/ui/sidebar-preferences';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Separator } from '@components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@components/ui/sheet';
import { Skeleton } from '@components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@components/ui/tooltip';

const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
// Keep in sync with SIDEBAR_WIDTH_MOBILE (assumes a 16px root font size).
const SIDEBAR_WIDTH_MOBILE_PX = 288;
// The sidebar should never default to open, and should auto-dismiss if open,
// when it would occupy more than a third of the viewport width.
const SIDEBAR_WIDTH_RATIO_THRESHOLD = 1 / 3;

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  /** Desktop panel width in px, persisted per browser. */
  width: number;
  setWidth: (width: number) => void;
  /** Writes a drag frame straight to the DOM without a React render. */
  previewWidth: (width: number, collapsed: boolean) => void;
  setResizing: (resizing: boolean) => void;
  panelId: string;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    defaultOpen?: boolean;
    defaultWidth?: number;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(
  (
    {
      defaultOpen = true,
      defaultWidth = SIDEBAR_DEFAULT_WIDTH,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const { ref: chromeRef, isChromeMobile, elementRef } = useChromeMobile();
    const isMobile = isChromeMobile;
    const [openMobile, setOpenMobile] = React.useState(false);

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        chromeRef(node);
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [chromeRef, ref]
    );

    const panelId = React.useId();

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        // Hand focus to the toggle before the collapsed panel becomes inert.
        if (!openState) {
          const panel = document.getElementById(panelId);
          if (panel?.contains(document.activeElement)) {
            elementRef.current
              ?.querySelector<HTMLElement>('[data-sidebar="trigger"]')
              ?.focus();
          }
        }
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }

        // Only a user's choice persists; automatic dismissal does not.
        writeSidebarCookie(SIDEBAR_OPEN_COOKIE, openState);
      },
      [setOpenProp, open, panelId, elementRef]
    );

    const [width, _setWidth] = React.useState(() =>
      clampSidebarWidth(defaultWidth)
    );
    const widthRef = React.useRef(width);
    widthRef.current = width;
    const setWidth = React.useCallback(
      (value: number) => {
        const next = clampSidebarWidth(value);
        elementRef.current?.style.setProperty('--sidebar-width', `${next}px`);
        _setWidth(next);
        writeSidebarCookie(SIDEBAR_WIDTH_COOKIE, next);
      },
      [elementRef]
    );
    const previewWidth = React.useCallback(
      (value: number, collapsed: boolean) => {
        const element = elementRef.current;
        if (!element) return;
        element.style.setProperty('--sidebar-width', `${value}px`);
        if (collapsed) element.dataset.snap = 'collapsed';
        else delete element.dataset.snap;
      },
      [elementRef]
    );
    const setResizing = React.useCallback(
      (resizing: boolean) => {
        const element = elementRef.current;
        if (element) {
          if (resizing) element.dataset.resizing = 'true';
          else delete element.dataset.resizing;
        }
        document.documentElement.toggleAttribute(
          'data-sidebar-resizing',
          resizing
        );
      },
      [elementRef]
    );

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
      return isMobile ? setOpenMobile(open => !open) : setOpen(open => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key.toLowerCase() === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey) &&
          !event.shiftKey &&
          !event.altKey &&
          !event.repeat &&
          !event.isComposing
        ) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    // Auto-dismiss (and default to closed) whenever the sidebar would take up
    // more than a third of the viewport width, e.g. a narrow phone or a
    // shrunk desktop window. The desktop width range keeps this from firing
    // at the chrome breakpoint; it guards future token changes.
    React.useEffect(() => {
      const checkWidthThreshold = () => {
        const containerWidth =
          elementRef.current?.clientWidth ?? window.innerWidth;
        const sidebarWidthPx = isMobile
          ? SIDEBAR_WIDTH_MOBILE_PX
          : widthRef.current + SIDEBAR_INSET;
        const exceedsThreshold =
          sidebarWidthPx / containerWidth > SIDEBAR_WIDTH_RATIO_THRESHOLD;

        if (!exceedsThreshold) return;

        if (isMobile) {
          setOpenMobile(false);
        } else {
          _setOpen(false);
        }
      };

      checkWidthThreshold();
      window.addEventListener('resize', checkWidthThreshold);
      return () => window.removeEventListener('resize', checkWidthThreshold);
    }, [isMobile, setOpenMobile, elementRef]);

    // Auto-dismiss the mobile sidebar whenever the user navigates.
    const pathname = usePathname();
    React.useEffect(() => {
      if (isMobile) {
        setOpenMobile(false);
      }
      // Only re-run when the route actually changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? 'expanded' : 'collapsed';

    const contextValue = React.useMemo<SidebarContextProps>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        width,
        setWidth,
        previewWidth,
        setResizing,
        panelId
      }),
      [
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        width,
        setWidth,
        previewWidth,
        setResizing,
        panelId
      ]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                '--sidebar-width': `${width}px`,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                ...style
              } as React.CSSProperties
            }
            className={cn(
              '@container/chrome group/sidebar-wrapper flex min-h-dvh w-full bg-background standalone:min-h-screen desktop-chrome:min-h-svh',
              className
            )}
            ref={setRefs}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
    /** Rendered beside the floating panel so the panel's clipping cannot hide it. */
    resizeHandle?: React.ReactNode;
  }
>(
  (
    {
      side = 'left',
      variant = 'sidebar',
      collapsible = 'offcanvas',
      resizeHandle,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { isMobile, state, openMobile, setOpenMobile, panelId } =
      useSidebar();

    if (collapsible === 'none') {
      return (
        <div
          className={cn(
            'flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground',
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className={cn(
              'w-(--sidebar-width) p-0 text-sidebar-foreground [&>button]:hidden',
              variant === 'floating'
                ? 'navigation-glass navigation-glass-menu'
                : 'bg-sidebar'
            )}
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH_MOBILE
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    if (variant === 'floating' && side === 'left') {
      // A floating glass panel inset from the window edges (macOS 26 style).
      // Content sits beside it on the shared canvas; only the spacer reserves
      // room, so the panel's width transition moves the page with it.
      return (
        <div
          ref={ref}
          className="group peer hidden text-sidebar-foreground desktop-chrome:block"
          data-state={state}
          data-collapsible={state === 'collapsed' ? collapsible : ''}
          data-variant={variant}
          data-side={side}
        >
          <div
            aria-hidden
            className={cn(
              'relative w-[calc(var(--sidebar-width)+var(--component-sidebar-inset))] transition-[width] duration-(--duration-normal) ease-out',
              'group-data-[collapsible=offcanvas]:w-0 group-data-[snap=collapsed]/sidebar-wrapper:w-0',
              'group-data-[resizing=true]/sidebar-wrapper:transition-none'
            )}
          />
          <div
            className={cn(
              'fixed inset-y-(--component-sidebar-inset) left-(--component-sidebar-inset) z-10 hidden w-(--sidebar-width) desktop-chrome:flex',
              'transition-[translate,visibility] duration-(--duration-normal) ease-out',
              'group-data-[collapsible=offcanvas]:invisible group-data-[collapsible=offcanvas]:-translate-x-[calc(100%+2*var(--component-sidebar-inset))]',
              'group-data-[snap=collapsed]/sidebar-wrapper:-translate-x-[calc(100%+2*var(--component-sidebar-inset))]',
              'group-data-[resizing=true]/sidebar-wrapper:transition-none',
              className
            )}
            {...props}
          >
            <div
              id={panelId}
              data-sidebar="sidebar"
              inert={state === 'collapsed' || undefined}
              className="navigation-glass navigation-glass-sidebar flex h-full w-full flex-col overflow-hidden rounded-xl"
            >
              {children}
            </div>
            {state === 'expanded' ? resizeHandle : null}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="group peer hidden text-sidebar-foreground desktop-chrome:block"
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-variant={variant}
        data-side={side}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating' || variant === 'inset'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
          )}
        />
        <div
          className={cn(
            'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear desktop-chrome:flex',
            side === 'left'
              ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
              : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
            // Adjust the padding for floating and inset variants.
            variant === 'floating' || variant === 'inset'
              ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
            className
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
Sidebar.displayName = 'Sidebar';

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button> & {
    label?: string;
    tooltip?: string;
  }
>(
  (
    { className, onClick, label = 'Toggle sidebar', tooltip, ...props },
    ref
  ) => {
    const { toggleSidebar, open, openMobile, isMobile, panelId } = useSidebar();

    const button = (
      <Button
        ref={ref}
        data-sidebar="trigger"
        variant="ghost"
        size="icon"
        aria-label={label}
        aria-expanded={isMobile ? openMobile : open}
        aria-controls={isMobile ? undefined : panelId}
        aria-keyshortcuts="Meta+B Control+B"
        className={cn(
          'shrink-0 rounded-full bg-card text-foreground hover:bg-accent [&_svg]:size-5',
          className
        )}
        onClick={event => {
          onClick?.(event);
          toggleSidebar();
        }}
        {...props}
      >
        <PanelLeft aria-hidden />
      </Button>
    );

    if (!tooltip) return button;

    return (
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom" align="start">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }
);
SidebarTrigger.displayName = 'SidebarTrigger';

type ResizeDrag = {
  x: number;
  start: number;
  next: number;
  collapsed: boolean;
  frame: number;
  handle: HTMLElement;
};

/**
 * Window-splitter handle on the floating panel's trailing edge: drag, arrow
 * keys (Shift for larger steps), Home/End, Enter or double-click to reset.
 * Dragging well past the minimum collapses the sidebar; Escape cancels.
 */
const SidebarResizeHandle = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<'div'>, 'children'> & {
    label: string;
    valueText?: (width: number) => string;
  }
>(({ className, label, valueText, ...props }, ref) => {
  const { width, setWidth, previewWidth, setResizing, setOpen, panelId } =
    useSidebar();
  const drag = React.useRef<ResizeDrag | null>(null);
  const escapeRef = React.useRef<(event: KeyboardEvent) => void>(() => {});

  const end = React.useCallback(
    (commit: boolean) => {
      const current = drag.current;
      if (!current) return;
      drag.current = null;
      cancelAnimationFrame(current.frame);
      setResizing(false);
      delete current.handle.dataset.dragging;
      window.removeEventListener('keydown', escapeRef.current, true);
      if (!commit) {
        previewWidth(current.start, false);
        current.handle.setAttribute('aria-valuenow', String(current.start));
      } else if (current.collapsed) {
        previewWidth(current.start, false);
        setOpen(false);
      } else {
        setWidth(current.next);
      }
    },
    [previewWidth, setOpen, setResizing, setWidth]
  );

  React.useEffect(() => {
    escapeRef.current = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      end(false);
    };
  }, [end]);

  React.useEffect(() => () => end(false), [end]);

  return (
    <div
      ref={ref}
      role="separator"
      aria-orientation="vertical"
      aria-controls={panelId}
      aria-label={label}
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
      aria-valuenow={width}
      aria-valuetext={valueText?.(width)}
      tabIndex={0}
      data-sidebar="resize-handle"
      data-at={
        width <= SIDEBAR_MIN_WIDTH
          ? 'min'
          : width >= SIDEBAR_MAX_WIDTH
            ? 'max'
            : undefined
      }
      className={cn(
        'absolute inset-y-6 -right-2 z-20 w-4 cursor-col-resize touch-none outline-none',
        'data-[at=max]:cursor-w-resize data-[at=min]:cursor-e-resize',
        // A short grabber pill appears on hover, keyboard focus and drag.
        'after:absolute after:left-1/2 after:top-1/2 after:h-12 after:w-1 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:transition-[background-color,height] after:duration-(--duration-fast)',
        'hover:after:bg-border-strong data-[dragging=true]:after:bg-ring',
        'focus-visible:after:h-16 focus-visible:after:bg-ring',
        className
      )}
      onPointerDown={event => {
        if (event.button !== 0 || !event.isPrimary) return;
        event.preventDefault();
        const handle = event.currentTarget;
        handle.setPointerCapture(event.pointerId);
        drag.current = {
          x: event.clientX,
          start: width,
          next: width,
          collapsed: false,
          frame: 0,
          handle
        };
        handle.dataset.dragging = 'true';
        setResizing(true);
        window.addEventListener('keydown', escapeRef.current, true);
      }}
      onPointerMove={event => {
        const current = drag.current;
        if (!current) return;
        const next = resolveSidebarDrag(
          current.start,
          event.clientX - current.x
        );
        current.next = next.width;
        current.collapsed = next.collapsed;
        current.frame ||= requestAnimationFrame(() => {
          current.frame = 0;
          previewWidth(current.next, current.collapsed);
          current.handle.setAttribute('aria-valuenow', String(current.next));
        });
      }}
      onPointerUp={() => end(true)}
      onPointerCancel={() => end(false)}
      onLostPointerCapture={() => end(true)}
      onDoubleClick={() => setWidth(SIDEBAR_DEFAULT_WIDTH)}
      onKeyDown={event => {
        const next = sidebarWidthForKey(width, event.key, event.shiftKey);
        if (next === null) return;
        event.preventDefault();
        setWidth(next);
      }}
      {...props}
    />
  );
});
SidebarResizeHandle.displayName = 'SidebarResizeHandle';

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'main'>
>(({ className, ...props }, ref) => {
  return (
    <main
      ref={ref}
      className={cn(
        'relative flex min-w-0 w-full flex-1 flex-col bg-background',
        'desktop-chrome:peer-data-[variant=inset]:m-2 desktop-chrome:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 desktop-chrome:peer-data-[variant=inset]:ml-0 desktop-chrome:peer-data-[variant=inset]:rounded-xl desktop-chrome:peer-data-[variant=inset]:shadow',
        className
      )}
      {...props}
    />
  );
});
SidebarInset.displayName = 'SidebarInset';

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        'h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        className
      )}
      {...props}
    />
  );
});
SidebarInput.displayName = 'SidebarInput';

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
});
SidebarHeader.displayName = 'SidebarHeader';

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
});
SidebarFooter.displayName = 'SidebarFooter';

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn('mx-2 w-auto bg-sidebar-border', className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = 'SidebarSeparator';

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      {...props}
    />
  );
});
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  );
});
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-muted-foreground outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className
      )}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        'absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = 'SidebarGroupAction';

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn('w-full text-sm', className)}
    {...props}
  />
));
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn('flex w-full min-w-0 flex-col gap-1', className)}
    {...props}
  />
));
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn('group/menu-item relative', className)}
    {...props}
  />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-full px-4 py-3 text-left text-body outline-none ring-sidebar-ring transition-colors duration-(--duration-fast) hover:bg-accent hover:text-foreground focus-visible:ring-2 active:bg-accent disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-navigation-selected data-[active=true]:font-medium data-[active=true]:text-brand-text data-[active=true]:hover:bg-navigation-selected data-[active=true]:hover:text-brand-text data-[state=open]:hover:bg-accent group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: '',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]'
      },
      size: {
        default: 'min-h-12 text-body',
        sm: 'min-h-11 text-caption',
        lg: 'min-h-14 text-body group-data-[collapsible=icon]:!p-0'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      tooltip,
      className,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === 'string') {
      tooltip = {
        children: tooltip
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== 'collapsed' || isMobile}
          {...tooltip}
        />
      </Tooltip>
    );
  }
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        'absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0',
        className
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      'pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground',
      'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
      'peer-data-[size=sm]/menu-button:top-1',
      'peer-data-[size=default]/menu-button:top-1.5',
      'peer-data-[size=lg]/menu-button:top-2.5',
      'group-data-[collapsible=icon]:hidden',
      className
    )}
    {...props}
  />
));
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5',
      'group-data-[collapsible=icon]:hidden',
      className
    )}
    {...props}
  />
));
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<'a'> & {
    asChild?: boolean;
    size?: 'sm' | 'md';
    isActive?: boolean;
  }
>(({ asChild = false, size = 'md', isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground',
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarResizeHandle,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar
};
